"use client";

import { publicStorePath } from "@/lib/storefront/public-url";
import { useState } from "react";
import Link from "next/link";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Mail, Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import { useSaleorAuthContext } from "@saleor/auth-sdk/react";
import { cn } from "@/lib/utils";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { getDefaultChannelSlug } from "@/lib/storefront/channel";
import posthog from "posthog-js";

import { isSalesCountryLocale } from "@/config/locale";
import { localizedAuthError } from "@/lib/locale/auth-error";
import { getSharedRuntimeCopy } from "@/config/shared-runtime-copy";

type FormMode = "login" | "setPassword";

interface LoginFormProps {
	containerClassName?: string;
}

export function LoginForm({ containerClassName }: LoginFormProps) {
	const router = useRouter();
	const params = useParams<{ locale: string }>();
	const channel = getDefaultChannelSlug();
	const searchParams = useSearchParams();
	const { signIn } = useSaleorAuthContext();
	const t = useTranslations("auth");
	const resolvedContainerClassName = cn("mx-auto w-full max-w-md", containerClassName ?? "mt-8 lg:mt-16");
	const surfaceClassName =
		"relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-[0_30px_60px_-25px_rgba(8,39,74,0.25)] sm:p-8";
	const destructiveNoticeClassName =
		"rounded-xl border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive";
	const infoNoticeClassName =
		"rounded-xl border border-info-border bg-info/35 p-3 text-sm text-info-foreground";
	const helperTextClassName =
		"text-center font-mono text-[0.62rem] uppercase tracking-[0.14em] leading-5 text-muted-foreground";

	// Check if this is a password reset callback
	const resetEmail = searchParams.get("email");
	const resetToken = searchParams.get("token");

	const [mode, setMode] = useState<FormMode>(resetEmail && resetToken ? "setPassword" : "login");

	const [email, setEmail] = useState(resetEmail || "");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [resetEmailSent, setResetEmailSent] = useState(false);
	const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

	// Update mode when URL params change (client-side navigation into the reset
	// link). Adjusted during render rather than in an effect so the form never
	// paints one frame in "login" mode before switching.
	const resetParamsKey = `${resetEmail ?? ""}:${resetToken ?? ""}`;
	const [lastResetParamsKey, setLastResetParamsKey] = useState(resetParamsKey);
	if (resetParamsKey !== lastResetParamsKey) {
		setLastResetParamsKey(resetParamsKey);
		if (resetEmail && resetToken) {
			setMode("setPassword");
			setEmail(resetEmail);
		}
	}

	const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

	const handleLogin = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		if (!email || !validateEmail(email)) {
			setError(t("invalidEmail"));
			return;
		}

		if (!password) {
			setError(t("enterPassword"));
			return;
		}

		setIsSubmitting(true);

		try {
			const result = await signIn({ email, password });

			if (result.data?.tokenCreate?.errors?.length) {
				const err = result.data.tokenCreate.errors[0];
				// Check for invalid credentials by message pattern
				const isInvalidCredentials =
					err.message?.toLowerCase().includes("invalid") ||
					err.message?.toLowerCase().includes("credentials");
				if (isSalesCountryLocale(params.locale)) {
					setError(localizedAuthError(params.locale, err, "login", t, t("signInFailed")));
				} else if (isInvalidCredentials) {
					setError(t("invalidCredentials"));
				} else {
					setError(err.message || t("signInFailed"));
				}
				return;
			}

			if (result.data?.tokenCreate?.token) {
				try {
					posthog.capture("user_logged_in", {
						source: "store",
						channel,
					});
				} catch {
					/* posthog not initialized */
				}
				const next = searchParams.get("next");
				const defaultDest = publicStorePath(params.locale, "/account");
				const safeNext =
					next && !next.includes("..") && (next === defaultDest || next.startsWith(`${defaultDest}/`))
						? next
						: defaultDest;
				router.push(safeNext);
				router.refresh();
			}
		} catch {
			setError(t("genericError"));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSetPassword = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		if (!password) {
			setError(t("enterNewPassword"));
			return;
		}

		if (password.length < 8) {
			setError(t("passwordMinLength"));
			return;
		}

		if (password !== confirmPassword) {
			setError(t("passwordsNoMatch"));
			return;
		}

		setIsSubmitting(true);

		try {
			const response = await fetch("/api/auth/set-password", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email: resetEmail,
					token: resetToken,
					password,
				}),
			});

			const data = (await response.json()) as {
				errors?: Array<{ message: string; code?: string; field?: string | null }>;
				success?: boolean;
			};

			if (data.errors?.length) {
				const err = data.errors[0];
				if (isSalesCountryLocale(params.locale)) {
					setError(localizedAuthError(params.locale, err, "reset", t, t("resetErrorFailed")));
				} else if (err.code === "INVALID_TOKEN" || err.message?.includes("token")) {
					setError(t("expiredResetLink"));
				} else {
					setError(err.message || "Failed to set password");
				}
				return;
			}

			if (data.success) {
				setPasswordResetSuccess(true);
				// Clear URL params and redirect to clean login
				setTimeout(() => {
					router.push(publicStorePath(params.locale, "/login"));
					router.refresh();
				}, 2000);
			}
		} catch {
			setError(t("genericError"));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleForgotPassword = async () => {
		setError("");
		setSuccessMessage("");

		if (!email) {
			setError(t("enterEmailFirst"));
			return;
		}

		if (!validateEmail(email)) {
			setError(t("invalidEmail"));
			return;
		}

		setIsSubmitting(true);

		try {
			const response = await fetch("/api/auth/reset-password", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email,
					channel,
					redirectUrl: `${window.location.origin}${publicStorePath(params.locale, "/login")}`,
				}),
			});

			const data = (await response.json()) as {
				errors?: Array<{ message: string; code?: string; field?: string | null }>;
				success?: boolean;
			};

			if (data.errors?.length) {
				setError(
					localizedAuthError(
						params.locale,
						data.errors[0],
						"reset",
						t,
						isSalesCountryLocale(params.locale) ? t("resetErrorFailed") : "Failed to send reset link",
					),
				);
				return;
			}

			setResetEmailSent(true);
			setSuccessMessage(t("resetEmailSent", { email }));
		} catch {
			setError(t("genericError"));
		} finally {
			setIsSubmitting(false);
		}
	};

	// Success state after password reset
	if (passwordResetSuccess) {
		return (
			<div className={resolvedContainerClassName}>
				<div className={surfaceClassName}>
					<div className="flex flex-col items-center gap-4 text-center">
						<div className="bg-success/10 flex h-16 w-16 items-center justify-center rounded-full">
							<CheckCircle className="h-8 w-8 text-success" />
						</div>
						<h1 className="text-3xl font-semibold tracking-tight text-foreground">{t("passwordUpdated")}</h1>
						<p className="max-w-sm text-sm leading-6 text-muted-foreground sm:text-base">
							{t("passwordResetSuccess")}
						</p>
						<p className="text-sm text-muted-foreground">{t("redirecting")}</p>
					</div>
				</div>
			</div>
		);
	}

	// Set New Password mode
	if (mode === "setPassword") {
		return (
			<div className={resolvedContainerClassName}>
				<div className={surfaceClassName}>
					<div className="mb-8 text-center">
						<h1 className="text-3xl font-semibold tracking-tight text-foreground">{t("setNewPassword")}</h1>
						<p className="mt-3 text-sm leading-6 text-muted-foreground">
							{t("enterNewPasswordFor")} <span className="font-medium">{resetEmail}</span>
						</p>
					</div>

					<form onSubmit={handleSetPassword} className="space-y-5">
						{error && <div className={destructiveNoticeClassName}>{error}</div>}

						{/* New Password */}
						<div className="space-y-1.5">
							<Label htmlFor="password" className="text-sm font-medium">
								{t("newPassword")}
							</Label>
							<div className="relative">
								<Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									id="password"
									type={showPassword ? "text" : "password"}
									placeholder={t("atLeast8Chars")}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									className="h-12 pl-10 pr-10"
									required
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
								>
									{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
								</button>
							</div>
						</div>

						{/* Confirm Password */}
						<div className="space-y-1.5">
							<Label htmlFor="confirmPassword" className="text-sm font-medium">
								{t("confirmPassword")}
							</Label>
							<div className="relative">
								<Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									id="confirmPassword"
									type={showConfirmPassword ? "text" : "password"}
									placeholder={t("confirmPasswordPlaceholder")}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									className="h-12 pl-10 pr-10"
									required
								/>
								<button
									type="button"
									onClick={() => setShowConfirmPassword(!showConfirmPassword)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
								>
									{showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
								</button>
							</div>
						</div>

						<Button type="submit" disabled={isSubmitting} className="h-12 w-full text-base font-semibold">
							{isSubmitting ? t("updating") : t("updatePassword")}
						</Button>

						<div className="text-center">
							<Link
								href={`${publicStorePath(params.locale, "/login")}`}
								className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground hover:no-underline"
							>
								{t("backToSignIn")}
							</Link>
						</div>
					</form>
				</div>
			</div>
		);
	}

	// Default Login mode
	return (
		<div className={resolvedContainerClassName}>
			<div className={surfaceClassName}>
				<div className="relative mb-8 text-center">
					<div className="flex items-center justify-center gap-2 font-mono text-[0.6rem] font-semibold uppercase tracking-[0.22em] text-primary-hero">
						<span aria-hidden className="bg-primary-hero/60 block h-px w-6" />
						{getSharedRuntimeCopy(params.locale, "auth.loginEyebrow")}
						<span aria-hidden className="bg-primary-hero/60 block h-px w-6" />
					</div>
					<h1 className="mt-4 font-display text-[clamp(1.5rem,4vw,2rem)] font-medium leading-tight tracking-[-0.022em] text-foreground">
						{t("welcomeBack")}
					</h1>
					<p className="mt-3 text-[0.85rem] leading-relaxed text-muted-foreground">
						{t("noAccount")}{" "}
						<Link
							href={`${publicStorePath(params.locale, "/signup")}`}
							className="font-mono text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-primary underline-offset-4 hover:text-primary-hero hover:underline"
						>
							{t("signUp")} →
						</Link>
					</p>
				</div>

				<form onSubmit={handleLogin} className="space-y-5">
					{error && <div className={destructiveNoticeClassName}>{error}</div>}

					{successMessage && <div className={infoNoticeClassName}>{successMessage}</div>}

					{/* Email */}
					<div className="space-y-1.5">
						<Label htmlFor="email" className="text-sm font-medium">
							{t("email")}
						</Label>
						<div className="relative">
							<Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								id="email"
								type="email"
								placeholder={t("emailPlaceholder")}
								value={email}
								onChange={(e) => {
									setEmail(e.target.value);
									setResetEmailSent(false);
								}}
								className="h-12 pl-10"
								required
							/>
						</div>
					</div>

					{/* Password */}
					<div className="space-y-1.5">
						<Label htmlFor="password" className="text-sm font-medium">
							{t("password")}
						</Label>
						<div className="relative">
							<Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								id="password"
								type={showPassword ? "text" : "password"}
								placeholder={t("passwordPlaceholder")}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className="h-12 pl-10 pr-10"
								required
							/>
							<button
								type="button"
								onClick={() => setShowPassword(!showPassword)}
								className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
							>
								{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
							</button>
						</div>
					</div>

					{/* Forgot Password */}
					<div className="flex justify-end">
						<button
							type="button"
							onClick={handleForgotPassword}
							disabled={isSubmitting}
							className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground hover:no-underline disabled:opacity-50"
						>
							{resetEmailSent ? t("resendLink") : t("forgotPassword")}
						</button>
					</div>

					<button
						type="submit"
						disabled={isSubmitting}
						className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary-hero font-mono text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-primary-hero-foreground shadow-md transition-colors hover:bg-primary disabled:opacity-60 sm:h-14 sm:text-[0.75rem]"
					>
						{isSubmitting ? t("signingIn") : t("signIn")}
					</button>

					<p className={helperTextClassName}>{t("formTrustNote")}</p>
				</form>
			</div>
		</div>
	);
}
